import { ApprovalStatusPolicy } from "constants/document/ApprovalStatusPolicy";
import { EMPTY_DOCUMENT_PREVIEW_SEARCH_CRITERIA } from "domain/entity/DocumentPreview/DocumentPreviewSearchCriteria";
import { getDefaultBuConfig } from "domain/repository/apiUtil";
import moment from "moment";
import { AclType } from "presentation/constant/ANAInfo/NbisivPermission";
import { Permission } from "presentation/constant/ANAInfo/PermissionName";
import { DocumentPreviewConstant } from "presentation/constant/DocumentPreview/DocumentPreviewConstant";
import { useDocumentPreviewVM } from "presentation/hook/DocumentPreview/useDocumentPreviewVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useDocumentPreviewTracked } from "presentation/store/DocumentPreview/DocumentPreviewProvider";
import { isPermissionExist } from "presentation/utils/permissionUtils";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { OverflowMenuButton } from "presentation/view/components/OverflowMenuButton";
import { memo, useCallback, useMemo } from "react";
import { HPHButton, IconButton } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const DocumentPreviewTitleBar: React.FC = () => {
    const [documentPreviewState] = useDocumentPreviewTracked();
    const documentPreviewVM = useDocumentPreviewVM();
    const { selectedRows, searchStatus, searchCriteria, currentSelectedData } = documentPreviewState;
    const DOCUMENT_PREVIEW_CONSTANT = DocumentPreviewConstant.Title;
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit, allowUpdate, userName, allowRead } = anaInfoState;
    //const allowReadDis = isPermissionExist(Permission.DOCUMENT_PREVIEW_DISAPPROVE, AclType.READ, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);
    const allowUpdateDis = isPermissionExist(Permission.DOCUMENT_PREVIEW_DISAPPROVE, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);
    //const allowReadApp = isPermissionExist(Permission.DOCUMENT_PREVIEW_APPROVE, AclType.READ, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);
    const allowUpdateApp = isPermissionExist(Permission.DOCUMENT_PREVIEW_APPROVE, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);
    const allowUpdateSL = isPermissionExist(Permission.DOCUMENT_PREVIEW_SL, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);
    const allowUpdateCC = isPermissionExist(Permission.DOCUMENT_PREVIEW_CC, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);

    const handleEdit = useCallback(() => {

        documentPreviewVM.onEditClick();
    }, [documentPreviewVM]);

    const handleAdjContainer = useCallback(() => {
        if (currentSelectedData && currentSelectedData.docType !== 'IN') {
            messageBarVM.showError('Please select record(s) with doc. type is "IN".');
            return;
        }
        documentPreviewVM.onShowLoading();
        documentPreviewVM.onAdjCntrClick(currentSelectedData).then((data) => {
            if (data !== 'success') {
                const errorMsg = (data === undefined || data === null)
                    ? "Adjust Container fail."
                    : data;
                messageBarVM.showError(errorMsg);
            }
        }).catch(error => {
            documentPreviewVM.onHideLoading();
            messageBarVM.showError("Adjust Containe fail.");
        }).finally(() => {
            documentPreviewVM.onHideLoading();
        })
    }, [documentPreviewVM, currentSelectedData, messageBarVM]);

    const handleFinalize = useCallback(async () => {
        documentPreviewVM.onShowPrintAndSent(userName, selectedRows);
    }, [documentPreviewVM, selectedRows, userName]);

    const handleReject = useCallback(async () => {
        documentPreviewVM.onShowLoading();
        try {
            const result = await documentPreviewVM.onReject(selectedRows);
            if (result !== "") {
                messageBarVM.showError(result);
            } else {
                messageBarVM.showSuccess("Withdraw Success.");
            }
            if (searchStatus !== "") {
                await documentPreviewVM.onGroupButtonClick(searchStatus, operatingUnit, allowUpdateSL, allowUpdateCC);
            } else {
                await documentPreviewVM.searchDocPreviewData(searchCriteria, operatingUnit, allowUpdateSL, allowUpdateCC);
            }
        } catch (error) {
            messageBarVM.showError("Withdraw fail.");
        } finally {
            documentPreviewVM.onHideLoading();
        }
    }, [documentPreviewVM, messageBarVM, searchCriteria, searchStatus, selectedRows, allowUpdateSL, allowUpdateCC, operatingUnit]);

    const handleRefresh = useCallback(async () => {
        documentPreviewVM.onShowLoading();
        let newSearchCriteria = null;
        newSearchCriteria = { ...EMPTY_DOCUMENT_PREVIEW_SEARCH_CRITERIA }
        newSearchCriteria = {
            ...newSearchCriteria,
            genBy: anaInfoState.userName,
            genDateFrom: moment().subtract(getDefaultBuConfig("REACT_APP_SEARCH_DATA_DEFAULT_DATE_RANGE"), "days").startOf("day").toDate(),
            genDateTo: moment().endOf("day").toDate(),
        };
        documentPreviewVM.searchAllDocPreviewData(operatingUnit, allowUpdateSL, allowUpdateCC);
        await documentPreviewVM.searchDocPreviewData(newSearchCriteria, operatingUnit, allowUpdateSL, allowUpdateCC).then((data) => {
            documentPreviewVM.onHideLoading();
        }).catch(error => {
            documentPreviewVM.onHideLoading();
        }).finally(() => {
            documentPreviewVM.onHideLoading();
        })
    }, [anaInfoState.userName, documentPreviewVM, operatingUnit, allowUpdateSL, allowUpdateCC]);

    const handlePreviewDoc = useCallback(() => {
        documentPreviewVM.previewDocReport(selectedRows, false);
    }, [selectedRows, documentPreviewVM]);

    const handleDownloadDoc = useCallback(() => {
        documentPreviewVM.previewDocReport(selectedRows, true);
    }, [documentPreviewVM, selectedRows]);

    const handlePreviewSupporting = useCallback(async () => {
        await documentPreviewVM.previewSupportingReport(selectedRows, false).then((data) => {
            if (data && data["previewReportFail"]) {
                messageBarVM.showError(data["previewReportFail"]);
            }
        });
    }, [documentPreviewVM, selectedRows, messageBarVM]);

    const handleDownloadSupporting = useCallback(async (exportType: string) => {
        await documentPreviewVM.previewSupportingReport(selectedRows, true, exportType).then((data) => {
            if (data && data["previewReportFail"]) {
                messageBarVM.showError(data["previewReportFail"]);
            }
        });
    }, [documentPreviewVM, selectedRows, messageBarVM]);

    const isDisable = () => {
        if (!documentPreviewState.isShowEditPanel && !documentPreviewState.isShowAdjCntrPanel && documentPreviewState.selectedRows.length > 0) return false;

        return true;
    }
    const isFinalizeDisable = () => {
        if (!documentPreviewState.isShowEditPanel && !documentPreviewState.isShowAdjCntrPanel && documentPreviewState.selectedRows.length > 0
            && documentPreviewState.selectedRows.every(entity => ApprovalStatusPolicy().getApprovalStatusKeyByValue(entity.approvalStatus as string) !== "WFA" && ApprovalStatusPolicy().getApprovalStatusKeyByValue(entity.approvalStatus as string) !== "REJ")) return false;

        return true;
    }

    const isEditDisable = () => {
        if (documentPreviewState.selectedRows.length > 0 && !documentPreviewState.isShowAdjCntrPanel && !documentPreviewState.isShowEditPanel) return false;

        return true;
    }

    const isAdjContainerDisable = () => {
        if (documentPreviewState.selectedRows.length === 1 && !documentPreviewState.isShowEditPanel && !documentPreviewState.isShowAdjCntrPanel) return false;

        return true;
    }

    const isInvPrevDisable = () => {
        if (!documentPreviewState.isShowEditPanel && !documentPreviewState.isShowAdjCntrPanel && documentPreviewState.selectedRows.length > 0) return false;

        return true;
    }

    const memoInvPrevMenu = useMemo(() => {
        return [
            {
                title: DOCUMENT_PREVIEW_CONSTANT.PREVIEW_DOC,
                disabled: false,
                command: () => {
                    handlePreviewDoc();
                }
            },
            {
                title: DOCUMENT_PREVIEW_CONSTANT.DOWNLOAD_DOC,
                disabled: false,
                command: () => {
                    handleDownloadDoc();
                }
            },
            {
                title: DOCUMENT_PREVIEW_CONSTANT.PREVIEW_SUP,
                disabled: false,
                command: () => {
                    handlePreviewSupporting();
                }
            },
            {
                title: DOCUMENT_PREVIEW_CONSTANT.DOWNLOAD_SUP,
                disabled: false,
                /*command: () => {
                    handleDownloadSupporting();
                }*/
                submenu: [
                    {
                        title: DocumentPreviewConstant.FileType.PDF,
                        disabled: false,
                        command: () => {
                            handleDownloadSupporting(DocumentPreviewConstant.FileType.PDF);
                        }
                    },
                    {
                        title: DocumentPreviewConstant.FileType.XLS,
                        disabled: false,
                        command: () => {
                            handleDownloadSupporting(DocumentPreviewConstant.FileType.XLS);
                        }
                    }
                ]
            }
        ]
    }, [DOCUMENT_PREVIEW_CONSTANT.DOWNLOAD_DOC, DOCUMENT_PREVIEW_CONSTANT.DOWNLOAD_SUP, DOCUMENT_PREVIEW_CONSTANT.PREVIEW_DOC, DOCUMENT_PREVIEW_CONSTANT.PREVIEW_SUP,
        handleDownloadDoc, handleDownloadSupporting, handlePreviewDoc, handlePreviewSupporting])

    return <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
        <HeaderTitle>{DOCUMENT_PREVIEW_CONSTANT.INVOICE}</HeaderTitle>
        <StyledAction className="tm-animated-wrapper">
            <IconButton fileName='Icon-reload' disabled={documentPreviewState.isShowEditPanel || documentPreviewState.isShowAdjCntrPanel} size='medium' toolTipText={'Refresh'} onClick={handleRefresh} />
            {/*<IconButton fileName='Icon-search' disabled={documentPreviewState.isShowEditPanel} size='medium' toolTipText={'Search'} toolTipArrow={false} onClick={handleSearch} />*/}
            {(operatingUnit === 'SAPT' || operatingUnit === 'KICT') && <>
                {(allowUpdate) && <IconButton fileName='Icon-pen' disabled={isEditDisable()} size='medium' toolTipText={'Edit'} toolTipArrow={false} onClick={handleEdit} />}
                {(allowUpdate) && <div className="add-seperator" />}
                {(allowUpdate) && <HPHButton disabled={isAdjContainerDisable()} label={DOCUMENT_PREVIEW_CONSTANT.ADJ_CONTAINER_BUT} size={'Small'} theme={'Secondary'} onClick={handleAdjContainer} />}
            </>}
            <div className="add-seperator" />
            {allowRead && <OverflowMenuButton disabled={isInvPrevDisable()} buttonLabel={DOCUMENT_PREVIEW_CONSTANT.INVOICE_PREVIEW_BUT} navigationList={memoInvPrevMenu} />}
            {(allowUpdateDis || allowUpdateApp) && <div className="add-seperator" />}
            {allowUpdateDis && <HPHButton disabled={isDisable()} label={DOCUMENT_PREVIEW_CONSTANT.REJECT_BUT} size={'Small'} theme={'Secondary'} onClick={handleReject} />}
            {allowUpdateApp && <HPHButton disabled={isFinalizeDisable()} label={DOCUMENT_PREVIEW_CONSTANT.FINALIZE_BUT} size={'Small'} theme={'Primary'} onClick={handleFinalize} />}
        </StyledAction>
        {documentPreviewState.forceRefresh && <></>}
    </Sidebarheader>
}

export default memo(DocumentPreviewTitleBar);